import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { PhaseStatus } from '@sr-test-wizard/shared';
import { api } from '../../lib/api';
import { useRunStore } from '../../state/runStore';
import { CriticalFilesCard } from './CriticalFilesCard';
import { CriticalFilesAiCard } from './CriticalFilesAiCard';
import { SourceCodeViewer } from './SourceCodeViewer';
import { SourceTree } from './SourceTree';
import { StatusIcon } from '../../components/StatusIcon';

function PhasePill({ status }: { status: PhaseStatus | undefined }) {
  const s = status ?? 'pending';
  return <span className={`phase-pill ${s}`}>{s.replace('_', ' ')}</span>;
}

/**
 * Phase 3 dashboard panel. Three stacked cards:
 *   1. Pull Source action with status pill + project path readout
 *   2. Critical Files (TEXEC / Manifest / hashes.log)
 *   3. Source Code Browser (tree + content viewer)
 *
 * NOTE: this panel still lives under `features/phase1/` for git-history
 * reasons; the visible step number changed to 3 when the rail was
 * reordered. See `App.tsx` for the URL <-> folder mapping.
 */
export function PullSourcePanel() {
  const { id } = useParams<{ id: string }>();
  const runId = id!;
  const {
    current,
    fileTree,
    fileTreeLoading,
    loadRun,
    setPhase,
    loadFileTree,
  } = useRunStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openFile, setOpenFile] = useState<string | null>(null);

  useEffect(() => {
    if (current?.project_path && !fileTree && !fileTreeLoading) {
      void loadFileTree(runId);
    }
  }, [runId, current?.project_path, fileTree, fileTreeLoading, loadFileTree]);

  const pullSource = async () => {
    setBusy(true);
    setError(null);
    setPhase('pull-source', 'in_progress');
    try {
      const queued = await api.post<{ started: boolean; jobId: string }>(
        `/api/runs/${runId}/pull-source`,
      );
      const jobId = queued.jobId;
      const deadline = Date.now() + 15 * 60 * 1000;
      while (Date.now() < deadline) {
        const job = await api.get<{ status: string; error: string | null }>(
          `/api/runs/jobs/${encodeURIComponent(jobId)}`,
        );
        if (job.status === 'completed') {
          await loadRun(runId);
          await loadFileTree(runId);
          return;
        }
        if (job.status === 'failed' || job.status === 'cancelled') {
          setError(job.error || `Pull source ${job.status}`);
          await loadRun(runId);
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      setError('Pull source is still running; refresh later for the result.');
      await loadRun(runId);
    } catch (err) {
      setError((err as Error).message);
      await loadRun(runId);
    } finally {
      setBusy(false);
    }
  };

  if (!current) return null;
  const status = current.phases?.['pull-source'];

  return (
    <div className="flex-col">
      <div className="card phase-action-card">
        <div className="row">
          <StatusIcon status={status ?? 'pending'} size={24} />
          <div className="phase-action-text">
            <h2 style={{ margin: 0 }}>Phase 3 - Pull Source Code</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              Resolves the Order key from the SR, downloads the GitLab archive (LFS-aware) and
              extracts it under the run folder.
            </p>
          </div>
          <span className="spacer" />
          <PhasePill status={status} />
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn btn-primary" disabled={busy} onClick={() => void pullSource()}>
            {busy
              ? status === 'in_progress'
                ? 'Pulling…'
                : 'Queuing…'
              : status === 'completed'
                ? 'Re-pull source'
                : 'Pull Source Code'}
          </button>
          {current.artifacts?.files === 'pruned' && (
            <button
              className="btn"
              disabled={busy}
              onClick={() =>
                void api.post(`/api/runs/${runId}/rehydrate`).then(() => loadRun(runId))
              }
            >
              Re-download pruned code
            </button>
          )}
          {current.project_path && (
            <span className="muted small mono" style={{ marginLeft: 12 }}>
              {current.project_path}
            </span>
          )}
          {current.provenance?.gitlab_commit_sha && (
            <span className="muted small mono" title="GitLab tip SHA at pull time">
              {current.provenance.gitlab_commit_sha.slice(0, 12)}
            </span>
          )}
        </div>
        {error && (
          <div className="muted small" style={{ marginTop: 8, color: 'var(--bad)' }}>
            {error}
          </div>
        )}
      </div>

      <CriticalFilesCard
        runId={runId}
        projectPath={current.project_path}
        onOpenFile={setOpenFile}
      />

      <CriticalFilesAiCard
        runId={runId}
        projectPath={current.project_path}
        onOpenFile={setOpenFile}
      />

      <div className="card">
        <div className="row">
          <strong>Source Code Browser</strong>
          <span className="spacer" />
          {fileTree && (
            <button
              className="btn btn-ghost small"
              onClick={() => void loadFileTree(runId)}
              disabled={fileTreeLoading}
            >
              {fileTreeLoading ? 'Refreshing...' : 'Refresh tree'}
            </button>
          )}
        </div>
        {!current.project_path && (
          <p className="muted small" style={{ marginTop: 8 }}>
            Pull the source code first to browse the extracted file tree.
          </p>
        )}
        {current.project_path && fileTreeLoading && !fileTree && (
          <p className="muted" style={{ marginTop: 8 }}>Loading file tree...</p>
        )}
        {fileTree && (
          <SourceTree root={fileTree} onSelect={setOpenFile} selected={openFile} />
        )}
      </div>

      {openFile && (
        <SourceCodeViewer
          runId={runId}
          path={openFile}
          onClose={() => setOpenFile(null)}
        />
      )}
    </div>
  );
}
